import React, { useCallback, useMemo, useRef, type ChangeEvent } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import ChevronDown from '@atlaskit/icon/glyph/chevron-down';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { SearchBar } from '../../../../common/ui/search-bar';
import { availableFilters, filterMessages, type Filter } from './constants';
import messages from './messages';

export type Props = {
	onSearch: (e: string) => void;
	onFilterChange: (filter?: Filter) => void;
	onToggleChange: (isChecked: boolean) => void;
	selectedFilter: Filter;
	showErrorToggle: boolean;
};

export const Filters = ({
	onSearch,
	onFilterChange,
	selectedFilter,
	onToggleChange,
	showErrorToggle,
}: Props) => {
	const { formatMessage } = useIntl();
	const isSearchEventSent = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const dropdownTrigger = useCallback(
		({ triggerRef, ...props }: CustomTriggerProps<HTMLElement>) => (
			<Button
				{...props}
				iconAfter={<ChevronDown size="medium" label="" />}
				ref={triggerRef}
				aria-label={formatMessage(messages.filterFieldsAriaLabel)}
			>
				{formatMessage(messages.fieldsPrefix, {
					filter: formatMessage(filterMessages[selectedFilter]),
				})}
			</Button>
		),
		[formatMessage, selectedFilter],
	);

	const onChangeOfFilter = useCallback(
		(filter: Filter) => {
			onFilterChange(filter);
			fireUIAnalytics(createAnalyticsEvent({}), 'fieldMappingFilter selected', { filter });
		},
		[createAnalyticsEvent, onFilterChange],
	);

	const filterList = useMemo(
		() =>
			availableFilters.map((filter) => (
				<DropdownItem
					onClick={() => onChangeOfFilter(filter)}
					key={filter}
					isSelected={selectedFilter === filter}
				>
					{formatMessage(filterMessages[filter])}
				</DropdownItem>
			)),
		[formatMessage, onChangeOfFilter, selectedFilter],
	);

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			onSearch(e.target.value);
			if (!isSearchEventSent.current) {
				fireUIAnalytics(createAnalyticsEvent({}), 'fieldMappingSearch searched');
				isSearchEventSent.current = true;
			}
		},
		[createAnalyticsEvent, onSearch],
	);

	return (
		<Flex
			justifyContent="space-between"
			xcss={wrapperStyles}
			testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.filters.flex"
		>
			<Flex gap="space.150">
				<Box xcss={searchWrapperStyles}>
					<SearchBar
						onSearchTermChange={onChange}
						placeholder={messages.searchPlacholder}
						isCompact
					/>
				</Box>
				<DropdownMenu
					trigger={dropdownTrigger}
					testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.filters.dropdown-menu"
				>
					<DropdownItemGroup>{filterList}</DropdownItemGroup>
				</DropdownMenu>
			</Flex>
			{showErrorToggle && (
				<Flex alignItems="center" gap="space.025">
					<Toggle
						onChange={(event) => onToggleChange(event.target.checked)}
						label={formatMessage(messages.showOnlyFieldsWithErrors)}
					/>
					<Box>{formatMessage(messages.showOnlyFieldsWithErrors)}</Box>
				</Flex>
			)}
		</Flex>
	);
};

const wrapperStyles = xcss({
	paddingBlock: 'space.300',
});

const searchWrapperStyles = xcss({
	width: '240px',
});
